import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Shield, Users, TrendingUp, Wallet } from "lucide-react";
import LeadForm from "../components/LeadForm";
import { SectionEyebrow, FadeInSection, ProofStrip, PartnerStrip, TestimonialsSection, FinalCTASection } from "../components/SharedSections";

const CHRIS_HEADSHOT = "https://static.wixstatic.com/media/8cad63_5d40e20bba3647b6b3c050cd2f415da5~mv2.jpg/v1/fill/w_245,h_367,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/8cad63_5d40e20bba3647b6b3c050cd2f415da5~mv2.jpg";
const CHRIS_TEAM_PHOTO = "https://static.wixstatic.com/media/8cad63_91c959af9ad5415babec429e8fde7588~mv2.jpg/v1/crop/x_0,y_984,w_4016,h_3327/fill/w_403,h_334,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Mayker%20Colour-20.jpg";
const KAI_PHOTO = "https://static.wixstatic.com/media/8cad63_9e375d858dde499baa3b54b9699fb3dc~mv2.jpg/v1/fill/w_403,h_334,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/8cad63_9e375d858dde499baa3b54b9699fb3dc~mv2.jpg";

const HomePage = () => {
  useEffect(() => {
    document.title = "Mayker Financial Group | Independent Financial Advisors in BC";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute("content", "Life insurance, group benefits, financial planning and investments for BC families and business owners. Independent advisors serving all of British Columbia since 2009.");
    }
  }, []);

  const services = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Life Insurance",
      description: "Protect your family with the right coverage at the right price. We compare top Canadian insurers to find your perfect fit.",
      link: "/life-insurance"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Group Benefits",
      description: "Attract and retain great people with a benefits plan tailored to your business, no matter the size.",
      link: "/group-benefits"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Financial Planning",
      description: "A complete roadmap for your financial future — retirement, estate, tax strategy, and more.",
      link: "/financial-planning"
    },
    {
      icon: <Wallet className="w-6 h-6" />,
      title: "TFSA & Investments",
      description: "Grow your wealth tax-efficiently with a personalized investment strategy built around your goals.",
      link: "/tfsa-investments"
    }
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="min-h-screen bg-[#0C1B33] relative overflow-hidden pt-24 lg:pt-28 pb-16 lg:pb-20 px-4 lg:px-12">
        <div className="hero-glow" />
        <div className="hero-bottom-line" />

        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_460px] gap-10 lg:gap-16 items-center">
          {/* Left column */}
          <div className="animate-fade-in-up">
            <SectionEyebrow light>British Columbia · Independent Advisors</SectionEyebrow>

            <h1 className="font-playfair text-3xl lg:text-5xl font-bold text-white leading-[1.15] mb-5">
              Expert financial planning for <em className="italic text-[#C9A84C]">families & business owners.</em>
            </h1>

            <p className="text-base font-light text-white/60 leading-relaxed mb-8 max-w-lg">
              We're independent advisors serving all of British Columbia — which means we work for you, not an insurance company. Life insurance, group benefits, investments, and financial planning under one roof.
            </p>

            {/* Chris intro card */}
            <div className="chris-intro-card flex items-center gap-4 p-4 rounded-lg mb-8">
              <img 
                src={CHRIS_HEADSHOT} 
                alt="Chris Johnstone"
                className="w-14 h-14 rounded-full object-cover object-top border-2 border-[#C9A84C] flex-shrink-0"
              />
              <div>
                <div className="text-sm font-medium text-white">Christopher Johnstone</div>
                <div className="text-xs text-[#C9A84C] mt-0.5">Founder & President, Mayker Financial Group</div>
                <div className="text-xs text-white/45 mt-1 leading-relaxed">
                  BC-based since 2009 · Queen's University Commerce · 15+ years advising families & business owners
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="flex gap-6 lg:gap-7 items-center flex-wrap">
              <div className="flex flex-col gap-0.5">
                <span className="font-playfair text-2xl lg:text-3xl font-semibold text-[#C9A84C]">15+</span>
                <span className="text-[11px] text-white/40 tracking-wide">Years advising</span>
              </div>
              <div className="w-px h-9 bg-white/10" />
              <div className="flex flex-col gap-0.5">
                <span className="font-playfair text-2xl lg:text-3xl font-semibold text-[#C9A84C]">500+</span>
                <span className="text-[11px] text-white/40 tracking-wide">Families served</span>
              </div>
              <div className="w-px h-9 bg-white/10" />
              <div className="flex flex-col gap-0.5">
                <span className="font-playfair text-2xl lg:text-3xl font-semibold text-[#C9A84C]">100%</span>
                <span className="text-[11px] text-white/40 tracking-wide">Independent</span>
              </div>
            </div>
          </div>

          {/* Right column - Lead Form */}
          <div 
            id="lead-form" 
            className="bg-[#FAF8F3] rounded-xl p-6 lg:p-8 shadow-[0_40px_100px_rgba(0,0,0,0.45)] relative card-top-accent animate-fade-in-up animate-delay-200"
          >
            <LeadForm phoneRequired intentRequired />
          </div>
        </div>
      </section>

      <ProofStrip />
      <PartnerStrip />

      {/* Services Overview */}
      <section className="bg-[#FAF8F3] py-16 lg:py-20 px-4 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <FadeInSection>
            <SectionEyebrow>What we do</SectionEyebrow>
            <h2 className="font-playfair text-2xl lg:text-4xl font-semibold text-[#0C1B33] leading-tight mb-4">
              Everything your financial future needs
            </h2>
            <p className="text-[15px] text-[#6B7A99] max-w-xl leading-relaxed mb-10 lg:mb-12">
              From protecting your family to growing your wealth — we provide comprehensive financial services tailored to your life.
            </p>
          </FadeInSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {services.map((service, index) => (
              <FadeInSection key={index}>
                <Link 
                  to={service.link}
                  className="service-card block bg-white border border-black/5 rounded-lg p-6 h-full hover:border-[#C9A84C]/30"
                >
                  <div className="w-12 h-12 bg-[#C9A84C]/10 rounded-lg flex items-center justify-center mb-4 text-[#C9A84C]">
                    {service.icon}
                  </div>
                  <h3 className="font-playfair text-lg font-semibold text-[#0C1B33] mb-2">
                    {service.title}
                  </h3>
                  <p className="text-sm text-[#6B7A99] leading-relaxed mb-3">
                    {service.description}
                  </p>
                  <span className="text-sm font-medium text-[#C9A84C]">
                    Learn more →
                  </span>
                </Link>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* Meet the Team */}
      <section className="bg-[#F0EDE4] py-16 lg:py-20 px-4 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <FadeInSection>
            <SectionEyebrow>Our team</SectionEyebrow>
            <h2 className="font-playfair text-2xl lg:text-4xl font-semibold text-[#0C1B33] leading-tight mb-10 lg:mb-12">
              Your Victoria-based advisors
            </h2>
          </FadeInSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {/* Chris */}
            <FadeInSection>
              <div className="bg-white rounded-lg overflow-hidden border border-black/5">
                <img 
                  src={CHRIS_TEAM_PHOTO}
                  alt="Christopher Johnstone"
                  className="w-full h-64 object-cover object-top"
                />
                <div className="p-6">
                  <h3 className="font-playfair text-xl font-semibold text-[#0C1B33]">Christopher Johnstone</h3>
                  <p className="text-sm text-[#C9A84C] mb-3">President & Founder</p>
                  <p className="text-sm text-[#6B7A99] leading-relaxed mb-4">
                    Chris founded Mayker in 2017 after nearly a decade building one of Victoria's most respected financial practices. A Queen's Commerce grad, he's served BC families for 15+ years.
                  </p>
                  <a 
                    href="https://www.linkedin.com/in/chris-johnstone-62764114/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-[#0C1B33] hover:text-[#C9A84C] transition-colors"
                  >
                    LinkedIn →
                  </a>
                </div>
              </div>
            </FadeInSection>

            {/* Kai */}
            <FadeInSection>
              <div className="bg-white rounded-lg overflow-hidden border border-black/5">
                <img 
                  src={KAI_PHOTO}
                  alt="Kai Simpson"
                  className="w-full h-64 object-cover object-top"
                />
                <div className="p-6">
                  <h3 className="font-playfair text-xl font-semibold text-[#0C1B33]">Kai Simpson</h3>
                  <p className="text-sm text-[#C9A84C] mb-3">Financial Advisor</p>
                  <p className="text-sm text-[#6B7A99] leading-relaxed mb-4">
                    Born and raised in Victoria, Kai is passionate about helping people plan for the future with clarity and confidence. He focuses on creating personalized strategies that reflect each client's goals.
                  </p>
                  <a 
                    href="https://www.linkedin.com/in/kai-simpson-512b25114/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-[#0C1B33] hover:text-[#C9A84C] transition-colors"
                  >
                    LinkedIn →
                  </a>
                </div>
              </div>
            </FadeInSection>
          </div>

          <FadeInSection>
            <div className="text-center mt-8">
              <Link 
                to="/about"
                className="inline-block text-sm font-medium text-[#0C1B33] hover:text-[#C9A84C] transition-colors"
              >
                Meet the full team →
              </Link>
            </div>
          </FadeInSection>
        </div>
      </section>

      <TestimonialsSection />
      <FinalCTASection />
    </div>
  );
};

export default HomePage;
